import {
  add,
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addWeeks,
  areIntervalsOverlapping,
  differenceInMinutes,
  intervalToDuration,
  isBefore,
  isEqual,
  max,
} from "date-fns";
import * as R from "ramda";
import { ExactlyScheduledNote, ExactSchedule, Note } from ".";
import { dbg } from "../../utils";

// Zone related
export const isOnCalendar = (note: Note) => note.schedule.type === "EXACT";
export const isInBacklog = (note: Note) => note.schedule.type === "NONE";
export const isInDayBacklog = (date: Date) => (note: Note) =>
  note.schedule.type === "DAY" && isEqual(date, note.schedule.day);
export const isInWeekBacklog = (date: Date) => (note: Note) =>
  note.schedule.type === "WEEK" && isEqual(date, note.schedule.week);
export const isInMonthBacklog = (date: Date) => (note: Note) =>
  note.schedule.type === "MONTH" && isEqual(date, note.schedule.month);
export const isOverdue = (note: Note) => {
  if (note.completed) {
    return false;
  }
  switch (note.schedule.type) {
    case "DAY":
      return isBefore(addDays(note.schedule.day, 1), new Date());
    case "WEEK":
      return isBefore(addWeeks(note.schedule.week, 1), addDays(new Date(), 2));
    case "MONTH":
      return isBefore(addMonths(note.schedule.month, 1), new Date());
    case "EXACT":
      return isBefore(note.schedule.end, new Date());
    case "NONE":
      return false;
  }
};

export const isCompleted = (note: Note) => {
  if (!note.completed) {
    return false;
  }
  switch (note.schedule.type) {
    case "DAY":
      return isBefore(addDays(note.schedule.day, 1), new Date());
    case "WEEK":
      return isBefore(addWeeks(note.schedule.week, 1), addDays(new Date(), 2));
    case "MONTH":
      return isBefore(addMonths(note.schedule.month, 1), new Date());
    case "EXACT":
      return isBefore(note.schedule.end, new Date());
    case "NONE":
      return false;
  }
};

export const putInBacklog: (note: Note) => Note = (note: Note) => ({
  ...note,
  schedule: { type: "NONE" },
});
export const putInDayBacklog: (day: Date) => (note: Note) => Note =
  (day: Date) => (note: Note) => ({ ...note, schedule: { type: "DAY", day } });
export const putInWeekBacklog: (week: Date) => (note: Note) => Note =
  (week: Date) => (note: Note) => ({
    ...note,
    schedule: { type: "WEEK", week },
  });

export const putInMonthBacklog: (month: Date) => (note: Note) => Note =
  (month: Date) => (note: Note) => ({
    ...note,
    schedule: { type: "MONTH", month },
  });

export const putInExactSchedule: (
  schedule: ExactSchedule
) => (note: Note) => ExactlyScheduledNote =
  (schedule: ExactSchedule) => (note: Note) => ({
    ...note,
    schedule,
  });

export const saveDuration = (n: ExactlyScheduledNote) => {
  return {
    ...n,
    duration: intervalToDuration(R.pick(["start", "end"], n.schedule)),
  };
};

export const addDefaultDuration = (date: Date) => {
  return addMinutes(date, 12);
};

export const addDurationOfNote = (note: Note) => (date: Date) => {
  if (note.duration !== undefined) {
    return add(date, note.duration);
  } else {
    if (note.schedule.type === "EXACT") {
      // LEGACY: shouldn't occur for new notes, remove after a while;
      return add(
        date,
        intervalToDuration(R.pick(["start", "end"], note.schedule))
      );
    }
    return addDefaultDuration(date);
  }
};

export const putToExactDateWithNoLayout: (start: Date) => (note: Note) => Note =
  (start: Date) => (note: Note) => ({
    ...note,
    schedule: {
      type: "EXACT",
      start,
      end: addDurationOfNote(note)(start),
      width: 1,
      offset: 0,
    },
  });

// Calendar-related
export type Group = { ids: string[]; start: Date; end: Date };
export const computeGroups = (notes: ExactlyScheduledNote[]) => {
  let groups: Group[] = [];
  notes.forEach((n, i) => {
    if (!R.includes(n.id, R.flatten(groups.map((g) => g.ids)))) {
      const start = n.schedule.start;
      let end = n.schedule.end;
      const candidates = R.drop(i + 1, notes);
      const winners = R.takeWhile((c) => {
        const isInGroup = c.schedule.start < end;
        if (isInGroup) {
          end = max([end, c.schedule.end]);
        }
        return isInGroup;
      }, candidates);

      groups = [
        ...groups,
        { ids: [n.id, ...winners.map((on) => on.id)], start, end },
      ];
    }
  });
  return groups;
};

type NoteInterval = { offset: number; width: number };

export const areSchedulesVerticallyOverlapping = (
  s1: ExactSchedule,
  s2: ExactSchedule
) => {
  return areIntervalsOverlapping(
    R.pick(["start", "end"], s1),
    R.pick(["start", "end"], s2)
  );
};

export const areSchedulesHorizontallyOverlapping = (
  a: NoteInterval,
  b: NoteInterval
) => {
  // console.log("collision between", a, b);
  if (a.offset === b.offset) {
    return true;
  }

  const aStart = a.offset;
  const bStart = b.offset;
  const aEnd = a.offset + a.width;
  const bEnd = b.offset + b.width;

  return (
    (aStart < bEnd && aStart > bStart) ||
    (bStart < aEnd && bStart > aStart) ||
    (aEnd < bEnd && aEnd > bStart) ||
    (bEnd < aEnd && bEnd > aStart)
  );
};

export const areSchedulesOverlapping = (
  s1: ExactSchedule,
  s2: ExactSchedule
) => {
  return (
    areSchedulesHorizontallyOverlapping(s1, s2) &&
    areSchedulesVerticallyOverlapping(s1, s2)
  );
};

export const centerOfMass = (s: ExactSchedule) => {
  return s.offset + s.width / 2;
};
